/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import CreateUserDialog from "components/CreateUserDialog";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav, setOpenConfigurator } from "context";

// Context
import { useAuth } from "context/AuthContext";
import { api } from "api";

function DashboardNavbar({ absolute, light, isMini, showTitle, companyName }) {
  const [navbarType, setNavbarType] = useState();
  const displayCompanyName = companyName || localStorage.getItem("company_name");
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const { logout } = useAuth();
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  // Create User Dialog State
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const handleLogoutConfirm = () => {
    logout();
    setOpenLogoutDialog(false);
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };



  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);



  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      return white.main; // Force white icons on black background
    },
  });

  // Gradient Animation Styles
  const animatedTextStyle = {
    background: "linear-gradient(to right, #ffffff, #a0a0a0, #ffffff)",
    backgroundSize: "200% auto",
    color: "#fff",
    backgroundClip: "text",
    textFillColor: "transparent",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    animation: "shine 3s linear infinite, slideInRight 1s ease-out forwards",
    fontWeight: "bold",
    fontSize: "1rem",
    "@keyframes shine": {
      "0%": { // Start
        backgroundPosition: "200% center",
      },
      "100%": { // End
        backgroundPosition: "0% center",
      },
    },
    "@keyframes slideInRight": {
      "0%": {
        transform: "translateX(100%)",
        opacity: 0,
      },
      "100%": {
        transform: "translateX(0)",
        opacity: 1,
      },
    }
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => {
        const { palette: { gradients }, functions: { linearGradient } } = theme;
        // Use Side Navbar pattern: linear gradient of dark main and dark state
        const backgroundValue = linearGradient(gradients.dark.main, gradients.dark.state);

        return {
          ...navbar(theme, { transparentNavbar, absolute, light, darkMode }),
          background: `${backgroundValue} !important`,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          paddingTop: "8px",
          paddingBottom: "8px",
        };
      }}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })} display="flex" alignItems="center">
          <Breadcrumbs icon="home" title={route[route.length - 1] === "dashboard" ? "Financial Dashboard" : route[route.length - 1]} route={route} light={true} showTitle={showTitle} />
        </MDBox>

        {/* Animated Company Name in Center of Navbar (Desktop Only) */}
        {displayCompanyName && (
          <MDBox
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              width: "auto",
            }}
          >
            <MDTypography variant="button" sx={animatedTextStyle}>
              {displayCompanyName}
            </MDTypography>
          </MDBox>
        )}

        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* Mobile Company Name (Replaces Search) */}
            {displayCompanyName && (
              <MDBox pr={1} sx={{ display: { xs: "block", md: "none" } }}>
                <MDTypography variant="button" sx={animatedTextStyle}>
                  {displayCompanyName}
                </MDTypography>
              </MDBox>
            )}

            <MDBox color="white" display="flex" alignItems="center">
              {/* Create User Icon */}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={() => setOpenCreateUser(true)}
              >
                <Icon sx={iconsStyle}>person_add</Icon>
              </IconButton>

              <IconButton sx={navbarIconButton} size="small" disableRipple onClick={() => setOpenLogoutDialog(true)}>
                <Icon sx={iconsStyle}>logout</Icon>
              </IconButton>
              <IconButton size="small" disableRipple color="inherit" sx={navbarMobileMenu} onClick={handleMiniSidenav}>
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              {/* Notification Icon Removed */}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Logout"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to sign out?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <MDButton onClick={handleLogoutCancel} color="error" variant="contained" sx={{ mr: 1 }}>
            Cancel
          </MDButton>
          <MDButton onClick={handleLogoutConfirm} color="success" variant="contained">
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <CreateUserDialog open={openCreateUser} onClose={() => setOpenCreateUser(false)} />

    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  showTitle: true,
  companyName: "",
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  showTitle: PropTypes.bool,
  companyName: PropTypes.string,
};

export default DashboardNavbar;
